import React, { useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Plus, Pencil, Trash2, Download, Upload, Loader2, FileText, ShieldCheck, Megaphone, Scale, AlertTriangle, LifeBuoy, ClipboardCheck, Folder } from 'lucide-react';
import api from '../../../utils/api';
import { useConfirm } from '../../../components/ConfirmDialog';
import { Panel, Pill, RowMenu, DataTable, LoadingBlock, ErrorBlock, useLoad, errMsg, fmtDate, words, downloadFile } from '../kit';
import { ConsentModal, ComplaintModal, ConflictModal, RiskModal, CrisisModal, ReportRegModal } from './modals';
import { REPORT_TYPES, daysFromToday, inText, LinkButton, ViewPicker } from './shared';

// Records tab: the registers a coordinator keeps but does not work in every day. Safety and
// compliance (consents, complaints, conflicts of interest, risks, crisis plans) and documents and
// reports, one section at a time.
export default function RecordsTab({ scId, participantName, initialSection = 'safety' }) {
  const [section, setSection] = useState(initialSection);
  return (
    <div className="space-y-5">
      <ViewPicker label="Records section" value={section} onChange={setSection} options={[
        { value: 'safety', label: 'Safety and compliance' },
        { value: 'documents', label: 'Documents and reports' },
      ]} />
      {section === 'safety' ? (
        <>
          <Register scId={scId} path="consents" noun="consent" icon={ShieldCheck} title="Consents" addLabel="Record consent" Modal={ConsentModal}
            empty="No consents recorded. Record what was agreed to, by whom, when, and when it ends."
            columns={(edit, del) => [
              { accessorKey: 'consent_type', header: 'Consent', cell: ({ getValue }) => <span className="font-semibold text-slate-900">{getValue()}</span> },
              { id: 'by', header: 'Given by', accessorFn: (c) => c.granted_by || '', cell: ({ row: { original: c } }) => `${c.granted_by || '—'}${c.relationship ? ` (${c.relationship})` : ''}` },
              { accessorKey: 'granted_at', header: 'Given', cell: ({ getValue }) => <span className="whitespace-nowrap">{getValue() ? fmtDate(getValue()) : '—'}</span> },
              { accessorKey: 'expires_at', header: 'Ends', cell: ({ row: { original: c } }) => <EndDate date={c.expires_at} live={c.status === 'active'} /> },
              { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Pill status={getValue()} size="xs" /> },
              menu(edit, del),
            ]} />
          <Register scId={scId} path="complaints" noun="complaint" icon={Megaphone} title="Complaints" addLabel="Log complaint" Modal={ComplaintModal}
            empty="No complaints logged. Record complaints here with their follow-up and escalation."
            columns={(edit, del) => [
              { accessorKey: 'summary', header: 'Summary', cell: ({ getValue }) => <p className="min-w-[200px] max-w-md line-clamp-2">{getValue()}</p> },
              { accessorKey: 'category', header: 'Category', cell: ({ getValue }) => getValue() || '—' },
              { accessorKey: 'severity', header: 'Severity', cell: ({ getValue }) => <Pill tone={['high', 'critical'].includes(getValue()) ? 'amber' : getValue() === 'medium' ? 'blue' : 'slate'} label={words(getValue())} size="xs" /> },
              { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Pill status={getValue()} size="xs" /> },
              { accessorKey: 'raised_at', header: 'Raised', cell: ({ getValue }) => <span className="whitespace-nowrap">{getValue() ? fmtDate(getValue()) : '—'}</span> },
              menu(edit, del),
            ]} />
          <Register scId={scId} path="conflicts" noun="conflict of interest" icon={Scale} title="Conflicts of interest" addLabel="Declare conflict" Modal={ConflictModal}
            empty="No conflicts declared. Record any conflict (ownership, referral, family, financial) and how it is managed."
            columns={(edit, del) => [
              { accessorKey: 'nature', header: 'Conflict', cell: ({ getValue }) => <p className="min-w-[200px] max-w-md line-clamp-2">{getValue()}</p> },
              { accessorKey: 'conflict_type', header: 'Type', cell: ({ getValue }) => (getValue() ? words(getValue()) : '—') },
              { id: 'told', header: 'Participant told', accessorFn: (c) => (c.participant_informed ? 1 : 0), cell: ({ row: { original: c } }) => (c.participant_informed
                ? <span className="whitespace-nowrap">Yes{c.participant_informed_date ? `, ${fmtDate(c.participant_informed_date)}` : ''}</span>
                : <Pill tone="amber" size="xs" label="Not yet" />) },
              { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Pill status={getValue()} size="xs" /> },
              { accessorKey: 'review_date', header: 'Review', cell: ({ getValue }) => <ReviewDate date={getValue()} /> },
              menu(edit, del),
            ]} />
          <Register scId={scId} path="risk-assessments" noun="risk" icon={AlertTriangle} title="Risk assessments" addLabel="Add risk" Modal={RiskModal}
            empty="No risks assessed. Record each hazard with its likelihood, consequence, rating and controls."
            columns={(edit, del) => [
              { accessorKey: 'hazard', header: 'Hazard', cell: ({ getValue }) => <p className="min-w-[180px] max-w-sm font-semibold text-slate-900 line-clamp-2">{getValue()}</p> },
              { accessorKey: 'category', header: 'Category', cell: ({ getValue }) => getValue() || '—' },
              { accessorKey: 'rating', header: 'Rating', cell: ({ getValue }) => (getValue() ? <Pill tone={['high', 'extreme'].includes(getValue()) ? 'amber' : getValue() === 'medium' ? 'blue' : 'slate'} label={words(getValue())} size="xs" /> : '—') },
              { accessorKey: 'controls', header: 'Controls', cell: ({ getValue }) => <p className="min-w-[180px] max-w-sm line-clamp-2">{getValue() || '—'}</p> },
              { accessorKey: 'review_date', header: 'Review', cell: ({ getValue }) => <ReviewDate date={getValue()} /> },
              menu(edit, del),
            ]} />
          <Register scId={scId} path="crisis-plans" noun="crisis plan" icon={LifeBuoy} title="Crisis and emergency plans" addLabel="Add plan" Modal={CrisisModal}
            empty="No crisis plan. Record triggers, warning signs, what to do and who to call."
            columns={(edit, del) => [
              { accessorKey: 'title', header: 'Plan', cell: ({ getValue }) => <span className="font-semibold text-slate-900">{getValue() || 'Crisis plan'}</span> },
              { accessorKey: 'triggers', header: 'Triggers', cell: ({ getValue }) => <p className="min-w-[180px] max-w-sm line-clamp-2">{getValue() || '—'}</p> },
              { accessorKey: 'emergency_contacts', header: 'Emergency contacts', cell: ({ getValue }) => <p className="min-w-[160px] max-w-xs line-clamp-2">{getValue() || '—'}</p> },
              { accessorKey: 'review_date', header: 'Review', cell: ({ getValue }) => <ReviewDate date={getValue()} /> },
              menu(edit, del),
            ]} />
        </>
      ) : (
        <>
          <Documents scId={scId} />
          <Register scId={scId} path="reports" noun="report" icon={ClipboardCheck} title="NDIA reports" addLabel="New report" Modal={ReportRegModal}
            modalProps={{ participantName }}
            empty="No reports yet. Add an 8-week, mid-plan or end-of-plan report, or draft one with AI and save it here."
            columns={(edit, del) => [
              { accessorKey: 'report_type', header: 'Type', cell: ({ getValue }) => <span className="whitespace-nowrap">{REPORT_TYPES[getValue()] || words(getValue())}</span> },
              { accessorKey: 'title', header: 'Title', cell: ({ row: { original: r } }) => (
                <div className="min-w-[160px]"><p className="text-slate-900">{r.title || '—'}</p>{r.version > 1 && <p className="text-[12px] text-slate-500">Version {r.version}</p>}</div>
              ) },
              { accessorKey: 'due_date', header: 'Due', cell: ({ row: { original: r } }) => (r.status === 'submitted'
                ? <span className="whitespace-nowrap">{r.due_date ? fmtDate(r.due_date) : '—'}</span>
                : <ReviewDate date={r.due_date} />) },
              { accessorKey: 'status', header: 'Status', cell: ({ getValue }) => <Pill status={getValue()} size="xs" /> },
              menu(edit, del, 'Open and edit'),
            ]} />
        </>
      )}
    </div>
  );
}

const menu = (edit, del, editLabel = 'Edit') => ({
  id: 'menu', header: '', enableSorting: false, meta: { width: 44 },
  cell: ({ row: { original } }) => (
    <RowMenu items={[
      { label: editLabel, icon: Pencil, onClick: () => edit(original) },
      { label: 'Delete', icon: Trash2, danger: true, onClick: () => del(original) },
    ]} />
  ),
});

// A date something should be looked at again: red once it has passed, amber within 30 days.
function ReviewDate({ date }) {
  if (!date) return <span className="text-slate-500">—</span>;
  const d = daysFromToday(date);
  const late = d != null && d < 0;
  const soon = !late && d != null && d <= 30;
  return (
    <span className="whitespace-nowrap">
      {fmtDate(date)}
      {(late || soon) && <span className={clsx('block text-[12px] font-semibold', late ? 'text-rose-700' : 'text-amber-800')}>{inText(d)}</span>}
    </span>
  );
}
// An end date (consent): passing it is a fact, so it never turns red.
function EndDate({ date, live }) {
  if (!date) return <span className="text-slate-500">—</span>;
  const d = daysFromToday(date);
  return (
    <span className="whitespace-nowrap">
      {fmtDate(date)}
      {live && d != null && d >= 0 && d <= 30 && <span className="block text-[12px] font-semibold text-amber-800">ends {inText(d)}</span>}
      {d != null && d < 0 && <span className="block text-[12px] text-slate-500">ended</span>}
    </span>
  );
}

function Register({ scId, path, noun, icon, title, addLabel, Modal, modalProps, empty, columns }) {
  const l = useLoad(`/support-coordination/${scId}/${path}`);
  const confirm = useConfirm();
  const [modal, setModal] = useState(null);
  const del = async (row) => {
    if (!await confirm(`Delete this ${noun}? It leaves this file and is kept in the records archive for the retention period.`, { title: `Delete ${noun}`, danger: true, confirmLabel: 'Delete' })) return;
    try { await api.delete(`/support-coordination/${path}/${row.id}`); toast.success(`The ${noun} is deleted`); l.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Panel icon={icon} title={title} sub={l.data && l.data.length ? `${l.data.length} on file` : undefined}
      actions={<LinkButton icon={Plus} onClick={() => setModal({})}>{addLabel}</LinkButton>}>
      <DataTable columns={columns((row) => setModal({ editing: row }), del)} data={l.data || []} loading={l.loading} error={l.error} onRetry={l.reload} dense
        empty={<p className="text-[13px] text-slate-500">{empty}</p>} />
      {modal && <Modal scId={scId} editing={modal.editing} {...(modalProps || {})} onClose={() => setModal(null)} onSaved={() => { setModal(null); l.reload(); }} />}
    </Panel>
  );
}

function Documents({ scId }) {
  const l = useLoad(`/support-coordination/${scId}/documents`);
  const confirm = useConfirm();
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('name', file.name);
      await api.post(`/support-coordination/${scId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} uploaded`); l.reload();
    } catch (e) { toast.error(errMsg(e, 'The upload didn’t work. Check the file is a PDF, image or Word document under 15 MB.')); } finally { setUploading(false); }
  };
  const open = async (d) => {
    try { await downloadFile(`/support-coordination/documents/${d.id}/download`, { filename: d.name }); }
    catch (e) { toast.error(errMsg(e, 'The document didn’t open. Try again.')); }
  };
  const del = async (d) => {
    if (!await confirm(`Delete “${d.name}”? It is kept in the records archive for the retention period.`, { title: 'Delete document', danger: true, confirmLabel: 'Delete' })) return;
    try { await api.delete(`/support-coordination/documents/${d.id}`); toast.success('Document deleted'); l.reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const docs = l.data || [];
  return (
    <Panel icon={Folder} title="Documents" sub="Plans, referrals, assessments and reports. PDF, image or Word, up to 15 MB."
      actions={(
        <label className={clsx('inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-[13px] font-semibold cursor-pointer focus-within:ring-2 focus-within:ring-indigo-500',
          uploading ? 'text-slate-500' : 'text-indigo-700 hover:bg-indigo-50')}>
          {uploading ? <Loader2 size={14} className="animate-spin" aria-hidden /> : <Upload size={14} aria-hidden />}
          {uploading ? 'Uploading…' : 'Upload'}
          <input type="file" className="sr-only" disabled={uploading} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; upload(f); }} />
        </label>
      )}>
      {l.error ? <ErrorBlock error={l.error} onRetry={l.reload} /> : !l.data ? <LoadingBlock rows={2} /> : !docs.length ? (
        <p className="text-[13px] text-slate-500">No documents yet. Upload the plan, referral and assessments so they are in one place.</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {docs.map((d) => (
            <li key={d.id} className="py-2.5 flex items-center gap-2">
              <button type="button" onClick={() => open(d)}
                className="flex-1 min-w-0 flex items-center gap-3 text-left rounded-lg -mx-1 px-1 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                <span className="w-8 h-8 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0"><FileText size={15} aria-hidden /></span>
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-medium text-slate-900 truncate">{d.name}</span>
                  <span className="block text-[12px] text-slate-500">{[d.category && words(d.category), d.file_size ? `${Math.max(1, Math.round(d.file_size / 1024))} KB` : null, fmtDate(d.created_at)].filter(Boolean).join(' · ')}</span>
                </span>
              </button>
              <RowMenu label={`Actions for ${d.name}`} items={[
                { label: 'Download', icon: Download, onClick: () => open(d) },
                { label: 'Delete', icon: Trash2, danger: true, onClick: () => del(d) },
              ]} />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
